import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import type { VerticalProfile } from '../types/vertical-profile.types';

interface VerticalProfileListProps {
  profiles: VerticalProfile[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onSelect: (profile: VerticalProfile) => void;
}

export function VerticalProfileList({
  profiles,
  isLoading,
  isError,
  onSelect,
}: VerticalProfileListProps) {
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 py-8 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="size-4 animate-spin text-gray-500" />
        Loading vertical profiles…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700"
        role="alert"
      >
        Could not load vertical profiles. You may need staff or admin access.
      </div>
    );
  }

  if (!profiles?.length) {
    return (
      <div className="rounded-md border border-gray-400 bg-gray-50 p-4 text-sm text-gray-600">
        No vertical profiles found. Run the INV-1094 migration or seed command to populate profiles.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Profile</TableHead>
          <TableHead>Group</TableHead>
          <TableHead>Archetype</TableHead>
          <TableHead>Version</TableHead>
          <TableHead>Generation policy</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {profiles.map((profile) => (
          <TableRow
            key={profile.id}
            className="cursor-pointer"
            onClick={() => onSelect(profile)}
          >
            <TableCell>
              <div className="font-medium text-black">{profile.payload.name}</div>
              <div className="text-sm text-gray-500">{profile.profile_id}</div>
            </TableCell>
            <TableCell>{profile.payload.group}</TableCell>
            <TableCell>{profile.payload.archetype}</TableCell>
            <TableCell>{profile.version ?? '—'}</TableCell>
            <TableCell>{profile.payload.generation_policy.level}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
